// Lib
import React from 'react';

// Utils
import {
    getCaption,
    getElementId,
    getFileProp,
    getImageHeight,
    getImageProp,
    getImageWidth,
    getMediaAudioOnly,
    getMediaOriginalHeight,
    getMediaOriginalWidth,
    getShowCaption,
} from '../../../../common/elements/utils/elementPropertyUtils';
import fileGetMediaPlayerType from '../utils/fileGetMediaPlayerType';
import { getData } from '../../attachments/attachmentsSelector';
import { getElementModalSize } from '../../modal/elementModalUtils';
import { getMainEditorKey } from '../../utils/elementEditorUtils';
import fileCanShowMedia from '../utils/fileCanShowMedia';
import { getFileDataFilename, getFileDataType } from '../utils/fileElementUtils';

// Components
import DetailViewUntyped from '../../../components/displayMode/DetailView';
import FilePreviewUntyped from '../fileDetailView/FilePreview';
import FileIcon from '../../../components/fileIcon/FileIcon';
import EditableTitle from '../../../components/editableTitle/EditableTitle';
import FileProcessingIndicator from '../fileDetailView/FileProcessingIndicator';
import FileSecondaryDetails from '../fileDetailView/FileSecondaryDetails';
import Caption from '../../../components/caption/Caption';

// Hooks
import useObserveWindowSize from '../../../utils/dom/useObserveWindowSize';

// Constants
import { MEDIA_TYPES } from '../../../../common/links/richMediaConstants';

// Types
import type * as Immutable from 'immutable';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

// Styles
import './FileModalDefaultView.scss';

// DetailView and FilePreview are untyped JS components; type the props we pass to them.
const DetailView = DetailViewUntyped as React.FC<{
    previewElement?: React.ReactNode;
    iconElement?: React.ReactNode;
    titleElement?: React.ReactNode;
    secondaryElement?: React.ReactNode;
}>;
const FilePreview = FilePreviewUntyped as React.FC<Record<string, unknown>>;

type FileModalDefaultViewProps = {
    element: ImMNElement;
    attachment?: Immutable.Map<string, unknown>;
    gridSize: number;
    showDownloadOption?: boolean;
};

const hasPlayableMedia = (element: ImMNElement) => {
    const mediaType = fileGetMediaPlayerType(element);
    return mediaType === MEDIA_TYPES.AUDIO || mediaType === MEDIA_TYPES.VIDEO;
};

const getContentWidth = ({ element, windowWidth }: { element: ImMNElement; windowWidth: number }) => {
    const mediaType = fileGetMediaPlayerType(element);

    if (mediaType === MEDIA_TYPES.AUDIO) return windowWidth - 200;

    // Make videos 4 times bigger so even smaller videos will fill the screen
    if (mediaType === MEDIA_TYPES.VIDEO) {
        const videoWidth = getMediaOriginalWidth(element) || getImageWidth(element) || 320;
        return videoWidth * 4;
    }

    return getImageWidth(element) || 1280;
};

const getContentHeight = ({
    element,
    gridSize,
    audioOnly,
}: {
    element: ImMNElement;
    gridSize: number;
    audioOnly: boolean;
}) => {
    const mediaType = fileGetMediaPlayerType(element);

    if (mediaType === MEDIA_TYPES.AUDIO || audioOnly) return gridSize * 5;

    // Make videos 4 times bigger so even smaller videos will fill the screen
    if (mediaType === MEDIA_TYPES.VIDEO) {
        const videoWidth = getMediaOriginalHeight(element) || getImageHeight(element) || 180;
        return videoWidth * 4;
    }

    return getImageHeight(element) || 720;
};

const getModalMinHeight = ({ element }: { element: ImMNElement }) =>
    hasPlayableMedia(element)
        ? // If the file is playable media - then allow the height to be set by the media
          0
        : // Otherwise allow it to use the standard minimum modal height
          undefined;

const FileModalDetailView: React.FC<FileModalDefaultViewProps> = (props) => {
    const { element, attachment, gridSize, showDownloadOption } = props;

    const [windowWidth, windowHeight] = useObserveWindowSize();

    const fileData = getFileProp(element) || getData(attachment);
    const imageData = getImageProp(element);

    const awaitingPreview = !imageData && fileCanShowMedia(element, fileData);
    // Only show "Processing" here; upload/download byte progress is shown by FileTransferToast.
    const showProgress = awaitingPreview;

    const audioOnly = getMediaAudioOnly(element);
    const fallbackTitle = getFileDataFilename(fileData);

    const imageWidth = getContentWidth({ element, windowWidth });
    const imageHeight = getContentHeight({ element, gridSize, audioOnly });
    const modalMinHeight = getModalMinHeight({ element });

    const caption = getShowCaption(element) && getCaption(element);

    const { modalWidth, modalHeight } = getElementModalSize({
        windowWidth,
        windowHeight,
        imageWidth,
        imageHeight,
        idealHeight: undefined,
        caption,
        footerSize: 55,
        modalMinHeight,
        modalMinWidth: 450,
        gridSize,
    });

    const editorKey = getMainEditorKey(props);

    return (
        <div className="FileModalDefaultView" style={{ width: modalWidth, minHeight: modalHeight }}>
            <DetailView
                previewElement={
                    <FilePreview {...props} widthPx={modalWidth} forcePreview audioOnly={audioOnly} displayFullSize />
                }
                iconElement={<FileIcon fileType={getFileDataType(fileData)} audioOnly={audioOnly} />}
                titleElement={
                    <EditableTitle
                        element={element}
                        elementId={getElementId(element)}
                        initialValue={fallbackTitle}
                        maxLength={500}
                        selectFirst
                        selectAllOnEdit
                        editorKey={editorKey}
                    />
                }
                secondaryElement={
                    showProgress ? (
                        <FileProcessingIndicator />
                    ) : (
                        <FileSecondaryDetails
                            element={element}
                            fileData={fileData}
                            showDownloadOption={showDownloadOption}
                            showOpenOption={false}
                        />
                    )
                }
            />

            <Caption element={element} textContent={getCaption(element)} captionVisible={getShowCaption(element)} />
        </div>
    );
};

export default FileModalDetailView;
